import React, { useState, useEffect } from 'react';
import { Users, DollarSign, MessageSquare, TrendingUp, AlertCircle, Star } from 'lucide-react';

const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 animate-pulse">
            <div className="h-12 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-400">Ошибка загрузки статистики</p>
          <p className="text-xs text-red-400/80 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Всего пользователей',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      iconColor: 'text-blue-500'
    },
    {
      title: 'Активных сегодня',
      value: stats?.active_users_today || 0,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      iconColor: 'text-green-500'
    },
    {
      title: 'Всего платежей',
      value: `${(stats?.total_payments || 0).toFixed(0)} ₽`,
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600',
      iconColor: 'text-yellow-500'
    },
    {
      title: 'Платежей сегодня',
      value: `${(stats?.payments_today || 0).toFixed(0)} ₽`,
      icon: DollarSign,
      color: 'from-orange-500 to-orange-600',
      iconColor: 'text-orange-500'
    },
    {
      title: 'Открытых тикетов',
      value: stats?.open_tickets || 0,
      icon: AlertCircle,
      color: 'from-red-500 to-red-600',
      iconColor: 'text-red-500'
    },
    {
      title: 'Всего вопросов',
      value: stats?.total_questions || 0,
      icon: MessageSquare,
      color: 'from-purple-500 to-purple-600',
      iconColor: 'text-purple-500'
    },
    {
      title: 'Вопросов сегодня',
      value: stats?.questions_today || 0,
      icon: MessageSquare,
      color: 'from-pink-500 to-pink-600',
      iconColor: 'text-pink-500'
    },
    {
      title: 'Новых отзывов',
      value: stats?.pending_reviews || 0,
      icon: Star,
      color: 'from-cyan-500 to-cyan-600',
      iconColor: 'text-cyan-500'
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${card.color} group-hover:scale-110 transition-transform duration-200`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-white">{card.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Дополнительные графики и информация */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Быстрый доступ */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">Быстрый доступ</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer">
              <span className="text-gray-300">Открытые тикеты</span>
              <span className="text-red-500 font-bold">{stats?.open_tickets || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer">
              <span className="text-gray-300">Неодобренные отзывы</span>
              <span className="text-yellow-500 font-bold">{stats?.pending_reviews || 0}</span>
            </div>
          </div>
        </div>

        {/* Активность */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">Активность сегодня</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Активные пользователи</span>
                <span className="text-white font-medium">{stats?.active_users_today || 0} / {stats?.total_users || 0}</span>
              </div>
              <div className="w-full bg-gray-900/50 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((stats?.active_users_today || 0) / (stats?.total_users || 1) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-400">Вопросов задано</span>
                <span className="text-white font-medium">{stats?.questions_today || 0}</span>
              </div>
              <div className="w-full bg-gray-900/50 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((stats?.questions_today || 0) / 100 * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;